import { PREMIUM_STRIPE_LINKS, PREMIUM_PRICES } from '../components/pricing/PricingPlanCards';
import { markPendingUpgrade } from '../lib/postCheckoutRefresh';
import { buildStripeCheckoutUrl } from './stripeCheckoutUrl';

export const STRIPE_BILLING_PORTAL = 'https://billing.stripe.com/p/login/fZu5kEaY6gHocY09Um48000';

export type PricingCtaAction = 'register' | 'stripe' | 'portal' | 'dashboard' | 'none';

export interface PricingCtaState {
  basicLabel: string;
  premiumLabel: string;
  /**
   * Clés i18n des mêmes libellés (`landing.plans.cta.*`), pour les surfaces
   * traduites comme la landing page. `basicLabel`/`premiumLabel` restent en
   * français : ils servent encore aux appelants non traduits.
   */
  basicLabelKey: string;
  premiumLabelKey: string;
  basicDisabled: boolean;
  premiumDisabled: boolean;
  hideBasicCta: boolean;
  basicAction: PricingCtaAction;
  premiumAction: PricingCtaAction;
}

export interface PricingUserContext {
  isLoggedIn: boolean;
  isPremium: boolean;
  isTrial: boolean;
  subscriptionType: string | null | undefined;
  /** true si l'essai a déjà été consommé (tryTrial / trialEndedAt côté Firestore). */
  triedTrial?: boolean;
}

export function getPricingCtaState(ctx: PricingUserContext): PricingCtaState {
  const { isLoggedIn, isPremium, isTrial, subscriptionType, triedTrial } = ctx;
  const sub = subscriptionType ?? '';

  if (!isLoggedIn) {
    return {
      basicLabel: 'Commencer gratuitement',
      premiumLabel: 'Démarrer mes 7 jours gratuits',
      basicLabelKey: 'landing.plans.cta.start_free',
      premiumLabelKey: 'landing.plans.cta.start_trial',
      basicDisabled: false,
      premiumDisabled: false,
      hideBasicCta: false,
      basicAction: 'register',
      premiumAction: 'register',
    };
  }

  const isPaidPremium = isPremium && !isTrial && sub.includes('Premium');
  const isLegacyBasicPaid = isPremium && !isTrial && sub.includes('Basic');
  const isTrialActive = isTrial && isPremium && sub.includes('Premium');
  const isTrialEndedOnBasic = isTrial && !isPremium;

  if (isPaidPremium || isLegacyBasicPaid) {
    return {
      basicLabel: 'Gérer mon abonnement',
      premiumLabel: 'Gérer mon abonnement',
      basicLabelKey: 'landing.plans.cta.manage',
      premiumLabelKey: 'landing.plans.cta.manage',
      basicDisabled: false,
      premiumDisabled: false,
      hideBasicCta: false,
      basicAction: 'portal',
      premiumAction: 'portal',
    };
  }

  if (isTrialActive) {
    return {
      basicLabel: '',
      premiumLabel: 'Débloquer mon analyse complète',
      basicLabelKey: '',
      premiumLabelKey: 'landing.plans.cta.unlock',
      basicDisabled: true,
      premiumDisabled: false,
      hideBasicCta: true,
      basicAction: 'none',
      premiumAction: 'stripe',
    };
  }

  if (isTrialEndedOnBasic) {
    return {
      basicLabel: 'Ton plan actuel',
      premiumLabel: 'Débloquer mon analyse complète',
      basicLabelKey: 'landing.plans.cta.current_plan',
      premiumLabelKey: 'landing.plans.cta.unlock',
      basicDisabled: true,
      premiumDisabled: false,
      hideBasicCta: false,
      basicAction: 'none',
      premiumAction: 'stripe',
    };
  }

  // Essai consommé et nettoyé côté serveur : Basic gratuit, plus d'essai à proposer → upgrade direct
  if (triedTrial && !isPremium && !isTrial) {
    return {
      basicLabel: 'Ton plan actuel',
      premiumLabel: 'Passer au Premium',
      basicLabelKey: 'landing.plans.cta.current_plan',
      premiumLabelKey: 'landing.plans.cta.upgrade',
      basicDisabled: true,
      premiumDisabled: false,
      hideBasicCta: false,
      basicAction: 'none',
      premiumAction: 'stripe',
    };
  }

  return {
    basicLabel: 'Démarrer mes 7 jours gratuits',
    premiumLabel: 'Démarrer mes 7 jours gratuits',
    basicLabelKey: 'landing.plans.cta.start_trial',
    premiumLabelKey: 'landing.plans.cta.start_trial',
    basicDisabled: false,
    premiumDisabled: false,
    hideBasicCta: false,
    basicAction: 'dashboard',
    premiumAction: 'stripe',
  };
}

export function buildPremiumStripeCheckoutUrl(
  billingInterval: 'monthly' | 'yearly',
  email?: string | null,
  uid?: string | null,
): string {
  return buildStripeCheckoutUrl(PREMIUM_STRIPE_LINKS[billingInterval], {
    email,
    clientReferenceId: uid ?? undefined,
  });
}

export function getPricingCardFootnotes(
  billingInterval: 'monthly' | 'yearly',
  isLoggedIn: boolean,
): { basicFootnote?: string; premiumFootnote?: string; basicFootnoteKey?: string; premiumFootnoteKey?: string; premiumFootnotePrice?: string } {
  if (isLoggedIn) {
    return {};
  }

  const premiumPrice = PREMIUM_PRICES[billingInterval === 'yearly' ? 'yearly' : 'monthly'];

  return {
    basicFootnote: 'Gratuit à vie. Aucune carte demandée.',
    premiumFootnote: `Essai complet, sans carte bancaire. Ensuite ${premiumPrice}/mois ou tu repasses en Basic gratuit.`,
    // Clés i18n des mêmes libellés, avec le prix à interpoler côté appelant
    // (`t(premiumFootnoteKey, { price: premiumFootnotePrice })`).
    basicFootnoteKey: 'landing.plans.cta_footnote_basic',
    premiumFootnoteKey: 'landing.plans.cta_footnote_premium',
    premiumFootnotePrice: premiumPrice,
  };
}

export function getPricingStickyMobileLabels(ctx: PricingUserContext): {
  basicLabel: string;
  premiumLabel: string;
  basicLabelKey: string;
  premiumLabelKey: string;
} {
  if (!ctx.isLoggedIn) {
    return { basicLabel: 'Commencer', premiumLabel: '7 jours gratuits', basicLabelKey: 'landing.plans.cta_short.start', premiumLabelKey: 'landing.plans.cta_short.trial' };
  }

  const { isPremium, isTrial, subscriptionType } = ctx;
  const sub = subscriptionType ?? '';
  const isPaidPremium = isPremium && !isTrial && sub.includes('Premium');
  const isLegacyBasicPaid = isPremium && !isTrial && sub.includes('Basic');
  const isTrialActive = isTrial && isPremium && sub.includes('Premium');
  const isTrialEndedOnBasic = isTrial && !isPremium;

  if (isPaidPremium || isLegacyBasicPaid) {
    return { basicLabel: 'Gérer', premiumLabel: 'Gérer', basicLabelKey: 'landing.plans.cta_short.manage', premiumLabelKey: 'landing.plans.cta_short.manage' };
  }

  if (isTrialActive) {
    return { basicLabel: '', premiumLabel: 'Débloquer', basicLabelKey: '', premiumLabelKey: 'landing.plans.cta_short.unlock' };
  }

  if (isTrialEndedOnBasic) {
    return { basicLabel: 'Ton plan', premiumLabel: 'Débloquer', basicLabelKey: 'landing.plans.cta_short.current_plan', premiumLabelKey: 'landing.plans.cta_short.unlock' };
  }

  // Essai déjà consommé → upgrade direct
  if (ctx.triedTrial && !isPremium && !isTrial) {
    return { basicLabel: 'Ton plan', premiumLabel: 'Passer au Premium', basicLabelKey: 'landing.plans.cta_short.current_plan', premiumLabelKey: 'landing.plans.cta_short.upgrade' };
  }

  return { basicLabel: 'Commencer', premiumLabel: '7 jours gratuits', basicLabelKey: 'landing.plans.cta_short.start', premiumLabelKey: 'landing.plans.cta_short.trial' };
}

/** Marque un upgrade en cours puis redirige vers Stripe (refresh auto au retour). */
export function redirectToPremiumStripeCheckout(
  billingInterval: 'monthly' | 'yearly',
  email?: string | null,
  uid?: string | null,
): void {
  markPendingUpgrade({ plan: 'premium', billingInterval });
  window.location.href = buildPremiumStripeCheckoutUrl(billingInterval, email, uid);
}
